import { Page } from "../enums/Page";
import { html } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { SafeService, SafeState } from "../services/SafeService";

export class SafeComponent extends HTMLElement {
    private readonly _gameEventService: GameEventService = new GameEventService();
    private _inputField?: HTMLInputElement;
    private _safeService: SafeService = new SafeService();

    public constructor() {
        super();
    }

    public connectedCallback(): void {
        // Attach a shadow to the component for scoped styling
        this.attachShadow({ mode: "open" });
        // Render the body of the component
        this.render();
    }

    private render(): void {
        // Only run the render method if the shadow root is attached
        if (!this.shadowRoot) return;
        // Construct the body of the element
        const body: HTMLElement = this.construct();
        // Replace the current body
        this.shadowRoot.replaceChildren(body);
    }

    public construct(): HTMLElement {
        // HTML en CSS binnen de shadow DOM
        const element: HTMLElement = html`
        <div>
            <style>
                @import url("https://fonts.cdnfonts.com/css/seven-segment");
                :host {
                    display: inline-block;
                    font-family: "Seven Segment", monospace;
                    margin-top: 20rem;
                }

                .container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                }

                p {
                    margin: 0px;
                    font-size: 22px;
                    font-weight: bold;
                    background: transparent;
                    color: #2bff00;
                    border: none;
                    text-align: center;
                    outline: none;
                    pointer-events: none;
                }

                button {
                    color: #2bff00;
                    background: #1b1b1b;
                    border: 2px solid #777;
                    padding: 5px;
                    border-radius: 5px;
                    text-align: center;
                    box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.5);
                    width: 60px;
                    height: 60px;
                    font-size: 22px;
                }

                .input-container {
                    background: #1b1b1b;
                    border: 2px solid #777;
                    padding: 5px;
                    border-radius: 5px;
                    text-align: center;
                    box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.5);
                }

                #safeInput {
                    width: 180px;
                    height: 40px;
                    font-size: 22px;
                    font-weight: bold;
                    background: transparent;
                    color: #2bff00;
                    border: none;
                    text-align: center;
                    outline: none;
                    pointer-events: none;
                }

                .numpad {
                    display: grid;
                    grid-template-columns: repeat(3, 78px);
                    gap: 5px;
                    background: radial-gradient(circle, #3a3a3a 0%, #1b1b1b 100%);
                    padding: 15px;
                    border-radius: 10px;
                    border: 2px solid #777;
                    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3);
                    max-width: 240px;
                    text-align: center;
                }

                input[type="button"] {
                    font-family: "Seven Segment", monospace;
                    color: #2bff00;
                    background: #1b1b1b;
                    border: 2px solid #777;
                    padding: 5px;
                    border-radius: 5px;
                    text-align: center;
                    box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.5);
                }
            </style>

            <div class="container">
                <div class="input-container">
                    <input type="text" id="safeInput" max=4 readonly>
                </div>
                <div class="numpad">
                    <input type="button" data-value="1" value="1">
                    <input type="button" data-value="2" value="2">
                    <input type="button" data-value="3" value="3">
                    <input type="button" data-value="4" value="4">
                    <input type="button" data-value="5" value="5">
                    <input type="button" data-value="6" value="6">
                    <input type="button" data-value="7" value="7">
                    <input type="button" data-value="8" value="8">
                    <input type="button" data-value="9" value="9">
                    <input type="button" data-value="0" value="0">
                    <input type="button" id="backspace" value="←">
                    <input type="button" id="enter" value="Enter">
                </div>
                <button id="close">close</button>
            </div>
        </div>
        `;

        element.querySelector<HTMLButtonElement>("#close")?.addEventListener("click", () => this._gameEventService.switchPage(Page.Canvas));

        // Inputveld opslaan
        this._inputField = element.querySelector("#safeInput")!;

        // Numerieke knoppen (1-9, 0) toevoegen aan het inputveld
        const buttons: NodeListOf<HTMLInputElement> = element.querySelectorAll(".numpad input[type='button']");
        buttons.forEach((button: HTMLInputElement) => {
            button.addEventListener("click", () => this.handleInput(button.value));
        });

        // Backspace en enter knoppen
        element.querySelector<HTMLInputElement>("#backspace")?.addEventListener("click", () => this.handleBackspace());
        element.querySelector<HTMLInputElement>("#enter")?.addEventListener("click", () => this.handleEnter());

        return element;
    }

    private handleInput(value: string): void {
        if (this._inputField!.value.length >= 4) return; // Maximaal 4 karakters

        if (value === "←") {
            return;
        }
        else {
            // Voeg het cijfer toe aan het inputveld
            this._inputField!.value += value;
        }
    }

    private handleBackspace(): void {
        // Verwijder alleen het laatste cijfer als er cijfers in het veld staan
        if (this._inputField!.value.length > 0) {
            // Verwijder het laatste cijfer
            this._inputField!.value = this._inputField!.value.slice(0, -1);
        }
    }

    private async handleEnter(): Promise<void> {
        const code: string = this._inputField!.value;

        // Check the code using SafeService
        const SafeState: SafeState = await this._safeService.checkcode(code);

        if (SafeState.correct) {
            this._gameEventService.switchPage(Page.Canvas);
        }

        else {
            this._inputField!.value = ""; // Reset the input field
            alert("Incorrect code. Please try again.");
        }
    }
}
